import axios from "axios";

export const submitProviderInvoice = async ({
  purchaseOrderId,
  filePath,
  fileName,
  invoiceNumber,
  amount,
  invoiceDate,
}: {
  purchaseOrderId: string;
  filePath: string;
  fileName?: string;
  invoiceNumber?: string;
  amount?: number;
  invoiceDate?: string;
}) => {
  try {
    const res = await axios.post("/provider-invoices/submit", {
      purchaseOrderId,
      filePath,
      fileName,
      invoiceNumber,
      amount,
      invoiceDate,
    });
    return res.data;
  } catch (err: any) {
    return {
      success: false,
      error: err?.response?.data?.message || err?.message || "Error submitting invoice",
    };
  }
};

export type PoLineKind = "item" | "fuel_surcharge" | "sales_tax" | "card_fee" | "other" | "adjustment";

export interface PoLineInput {
  id?: string;
  name?: string;
  description?: string | null;
  quantity: number;
  cost: number;
  kind?: PoLineKind;
}

export interface ReviewContext {
  invoice: { id: string; invoiceNumber: string | null; amount: number | null; status: string };
  po: {
    id: string;
    sequence: number | null;
    version: number;
    total: number;
    lines: Array<{ id: string; name: string; description: string | null; quantity: number; cost: number; unit: string | null; kind: PoLineKind }>;
  };
  otherInvoices: Array<{ id: string; invoiceNumber: string | null; amount: number | null; status: string; deposit: boolean; zohoBillId: string | null; createdAt: string }>;
  bill:
    | null
    | { billId: string; unreadable: true }
    | {
        billId: string;
        billNumber: string | null;
        status: string | null;
        total: number;
        balance: number;
        paid: number;
        payments: Array<{ date: string | null; amount: number; reference: string | null }>;
      };
  path: "create" | "adopt" | "replace" | "update_in_place";
}

/** Everything the approve panel needs: PO lines, other invoices, the Zoho bill and its payments. */
export const getProviderInvoiceReviewContext = async (
  id: string,
): Promise<{ success: boolean; data?: ReviewContext; error?: string }> => {
  try {
    const res = await axios.get(`/provider-invoices/${id}/review-context`);
    return res.data;
  } catch (err: any) {
    return {
      success: false,
      error: err?.response?.data?.message || err?.message || "Error loading the review",
    };
  }
};

export const approveProviderInvoice = async (
  id: string,
  options?: {
    notes?: string;
    approveWithVariance?: boolean;
    invoiceNumber?: string;
    amount?: number;
    poLines?: PoLineInput[];
    deposit?: boolean;
  },
) => {
  try {
    const res = await axios.post(`/provider-invoices/${id}/approve`, {
      notes: options?.notes,
      approveWithVariance: options?.approveWithVariance,
      invoiceNumber: options?.invoiceNumber,
      amount: options?.amount,
      poLines: options?.poLines,
      deposit: options?.deposit,
    });
    return res.data;
  } catch (err: any) {
    return {
      success: false,
      error: err?.response?.data?.message || err?.message || "Error approving invoice",
    };
  }
};

export const rejectProviderInvoice = async (id: string, notes?: string) => {
  try {
    const res = await axios.post(`/provider-invoices/${id}/reject`, { notes });
    return res.data;
  } catch (err: any) {
    return {
      success: false,
      error: err?.response?.data?.message || err?.message || "Error rejecting invoice",
    };
  }
};

export const getPendingProviderInvoices = async () => {
  try {
    const res = await axios.get("/provider-invoices/pending");
    return res.data;
  } catch (err: any) {
    return {
      success: false,
      data: [],
      error: err?.response?.data?.message || err?.message || "Error fetching pending invoices",
    };
  }
};

export const getInvoicesForPO = async (purchaseOrderId: string) => {
  try {
    const res = await axios.get(`/provider-invoices/purchase-order/${purchaseOrderId}`);
    return res.data;
  } catch (err: any) {
    return {
      success: false,
      data: [],
      error: err?.response?.data?.message || err?.message || "Error fetching invoices",
    };
  }
};

export const getMyProviderInvoices = async () => {
  try {
    const res = await axios.get("/provider-invoices/my-invoices");
    return res.data;
  } catch (err: any) {
    return {
      success: false,
      data: [],
      error: err?.response?.data?.message || err?.message || "Error fetching invoices",
    };
  }
};

export const getProviderInvoiceFileUrl = async (id: string) => {
  try {
    const res = await axios.get(`/provider-invoices/${id}/file-url`, {
      excludeInterceptor: true,
    } as any);
    return res.data;
  } catch (err: any) {
    return {
      success: false,
      error: err?.response?.data?.message || err?.message || "Error fetching file URL",
    };
  }
};

export const getProviderInvoice = async (id: string) => {
  try {
    const res = await axios.get(`/provider-invoices/${id}`);
    return res.data;
  } catch (err: any) {
    return {
      success: false,
      error: err?.response?.data?.message || err?.message || "Error fetching invoice",
    };
  }
};
